import { useEffect, useState, useCallback } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/lib/i18n';

const API = process.env.REACT_APP_BACKEND_URL;

// VAPID public key (base64url) → Uint8Array for pushManager.subscribe()
function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Browser push opt-in button.
 * - `mode="user"` uses user JWT (localStorage.user_token) → POST /api/push/subscribe
 * - `mode="admin"` uses admin JWT (localStorage.access_token) → POST /api/admin/push/subscribe
 */
export default function PushSubscribeButton({ mode = 'user', compact = false }) {
  const { t } = useLang();
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const tokenKey = mode === 'admin' ? 'access_token' : 'user_token';
  const subscribeUrl = mode === 'admin' ? '/api/admin/push/subscribe' : '/api/push/subscribe';

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem(tokenKey);
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, [tokenKey]);

  // Detect existing subscription on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
      setSupported(false);
      return;
    }
    navigator.serviceWorker.getRegistration('/').then(reg => {
      if (!reg) return;
      reg.pushManager.getSubscription().then(sub => setSubscribed(!!sub));
    }).catch(() => {});
  }, []);

  const enable = async () => {
    if (!supported) {
      toast.error(t('push.unsupported'));
      return;
    }
    setLoading(true);
    try {
      // 1. Ensure service worker is registered
      let reg = await navigator.serviceWorker.getRegistration('/');
      if (!reg) reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' });
      await navigator.serviceWorker.ready;

      // 2. Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error(t('push.permissionDenied'));
        setLoading(false);
        return;
      }

      // 3. Fetch VAPID public key
      const keyRes = await fetch(`${API}/api/push/public-key`);
      const keyData = await keyRes.json();
      if (!keyData.publicKey) throw new Error('No VAPID key');

      // 4. Subscribe in the browser
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(keyData.publicKey),
        });
      }

      // 5. Ship subscription to backend
      const subJson = sub.toJSON();
      const res = await fetch(`${API}${subscribeUrl}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          isAdmin: mode === 'admin',
        }),
      });
      if (!res.ok) throw new Error('subscribe failed');
      setSubscribed(true);
      toast.success(t('push.enabled'));
    } catch (err) {
      console.error('Push subscribe failed', err);
      toast.error(t('push.enableFailed'));
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      const endpoint = sub ? sub.endpoint : '';
      if (sub) await sub.unsubscribe();
      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ endpoint }),
      }).catch(() => {});
      setSubscribed(false);
      toast.success(t('push.disabled'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;
  // Hide the toggle entirely when the user has denied permission at the
  // browser level — the button can't recover from that state and clicking
  // it does nothing useful, so showing "Push blocked" is just visual noise.
  if (permission === 'denied') return null;

  const isOn = subscribed && permission === 'granted';

  if (compact) {
    return (
      <button
        type="button"
        data-testid={mode === 'admin' ? 'admin-push-toggle' : 'cabinet-push-toggle'}
        onClick={isOn ? disable : enable}
        disabled={loading}
        title={isOn ? t('push.disable') : t('push.enable')}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${
          isOn
            ? 'bg-[var(--color-primary)]/20 text-[var(--color-primary)] border border-[var(--color-primary)]/30'
            : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
        }`}
      >
        {isOn ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={mode === 'admin' ? 'admin-push-toggle' : 'cabinet-push-toggle'}
      onClick={isOn ? disable : enable}
      disabled={loading}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-colors disabled:opacity-50 ${
        isOn
          ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border border-[var(--color-primary)]/30 hover:bg-[var(--color-primary)]/20'
          : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
      }`}
    >
      {isOn ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
      <span>{isOn ? t('push.on') : t('push.enable')}</span>
    </button>
  );
}
